import { Capacitor } from "@capacitor/core";

const SERVER_ID = "in.healthyowl.app";

export const remoteLog = (level: 'log' | 'warn' | 'error', message: string, data?: any) => {
  const serialized = data !== undefined ? (() => { try { return JSON.stringify(data); } catch { return String(data); } })() : undefined;
  console[level]('[Biometric]', message, data ?? '');
  fetch('/api/debug/client-log', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ level, tag: 'Biometric', message, data: serialized, timestamp: new Date().toISOString() }),
  }).catch(() => {});
};

export const deleteBiometricCredentials = async (): Promise<void> => {
  if (!Capacitor.isPluginAvailable("NativeBiometric")) return;
  try {
    const { NativeBiometric } = await import("capacitor-native-biometric");
    await NativeBiometric.deleteCredentials({ server: SERVER_ID });
    remoteLog('log', 'Credentials cleared on logout');
  } catch {
  }
};

export const useBiometric = () => {
  const isPluginAvailable = () => {
    return Capacitor.isPluginAvailable("NativeBiometric");
  };

  const isAvailable = async () => {
    if (!isPluginAvailable()) return false;
    try {
      const { NativeBiometric } = await import("capacitor-native-biometric");
      const result = await NativeBiometric.isAvailable();
      return result.isAvailable;
    } catch (e) {
      remoteLog('warn', 'isAvailable check failed', String(e));
      return false;
    }
  };

  const authenticate = async () => {
    remoteLog('log', `authenticate() called, plugin available: ${isPluginAvailable()}`);
    if (!isPluginAvailable()) return false;
    try {
      const { NativeBiometric } = await import("capacitor-native-biometric");
      remoteLog('log', 'calling verifyIdentity()');
      await NativeBiometric.verifyIdentity({
        reason: "Login to Healthy Owl",
        title: "Biometric Login",
        subtitle: "Use fingerprint or Face ID",
        description: "Place your finger on the sensor",
      });
      remoteLog('log', 'verifyIdentity() succeeded');
      return true;
    } catch (e: any) {
      remoteLog('warn', 'verifyIdentity() threw', { code: e?.code, message: e?.message });
      return false;
    }
  };

  const saveToken = async (refreshToken: string, userEmail: string) => {
    if (!isPluginAvailable()) {
      remoteLog('warn', 'Plugin not available, cannot save token');
      return false;
    }
    try {
      const { NativeBiometric } = await import("capacitor-native-biometric");
      await NativeBiometric.setCredentials({
        username: userEmail,
        password: refreshToken,
        server: SERVER_ID,
      });
      remoteLog('log', `Token saved successfully for ${userEmail}`);
      return true;
    } catch (e: any) {
      remoteLog('error', 'Failed to save token to keychain', { code: e?.code, message: e?.message });
      return false;
    }
  };

  const getToken = async (): Promise<{ refreshToken: string; userEmail: string } | null> => {
    remoteLog('log', `getToken() called, plugin available: ${isPluginAvailable()}`);
    if (!isPluginAvailable()) return null;
    try {
      const { NativeBiometric } = await import("capacitor-native-biometric");
      remoteLog('log', `getToken() calling getCredentials, server: ${SERVER_ID}`);
      const credentials = await NativeBiometric.getCredentials({
        server: SERVER_ID,
      });
      remoteLog('log', 'getToken() raw result', {
        hasUsername: !!credentials?.username,
        hasPassword: !!credentials?.password,
        passwordLength: credentials?.password?.length,
      });
      if (credentials && credentials.username && credentials.password) {
        return {
          userEmail: credentials.username,
          refreshToken: credentials.password,
        };
      }
      remoteLog('warn', 'getToken(): credentials found but missing username or password');
      return null;
    } catch (e: any) {
      remoteLog('warn', 'getToken() threw', { code: e?.code, message: e?.message });
      return null;
    }
  };

  const hasStoredToken = async (): Promise<boolean> => {
    if (!isPluginAvailable()) return false;
    try {
      const { NativeBiometric } = await import("capacitor-native-biometric");
      const credentials = await NativeBiometric.getCredentials({
        server: SERVER_ID,
      });
      return !!(credentials && credentials.username && credentials.password);
    } catch {
      return false;
    }
  };

  const deleteCredentials = async () => {
    if (!isPluginAvailable()) return;
    try {
      const { NativeBiometric } = await import("capacitor-native-biometric");
      await NativeBiometric.deleteCredentials({
        server: SERVER_ID,
      });
      console.log("[Biometric] Credentials deleted successfully");
    } catch (e) {
      console.warn("[Biometric] Failed to delete credentials:", e);
    }
  };

  return {
    isAvailable,
    authenticate,
    saveToken,
    getToken,
    hasStoredToken,
    deleteCredentials,
    isPluginAvailable,
  };
};
